import React, { useState, useEffect, useRef } from "react";
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./Modal.css";
import "./BuildScheduleDialog.css";
import Common from "../../Common/Common";

const NEW_SCHEDULE_VALUE = "__new__";
// Replace "X": empty that schedule first, then build into it — for rebuilding
// a schedule (typically "Main") from a new selection of places without
// creating a second schedule or appending after the old events. The choice
// is "__replace__" + the schedule's name. Added 2026-10-12.
const REPLACE_PREFIX = "__replace__";

// Shared with BuildMyTripWizard.js and DestinationsScreen.js's own "Auto-
// Build"/"Let Me Pick" offer — see Common.PACE_OPTIONS's comment.
const PACE_OPTIONS = Common.PACE_OPTIONS;

// Options step for "Build Trip from Places" — appears after the place
// picker, before the schedule is actually built. Lets the result go into a
// new schedule (default, matching the old one-click behavior) or append onto
// an existing one, and lets the start date be chosen instead of always
// defaulting to tomorrow. This is what let "Build Trip from Places"
// absorb "Create Events From Places" as one feature instead of two.
// scheduleNextDates — optional {scheduleName: Date} map, the day right after
// each existing schedule's last event. When the user picks "Add to X" from
// the Schedule dropdown, the start date auto-jumps to that day instead of
// whatever the "new schedule" default was — appending a "day 2" onto an
// existing 1-day schedule needs to actually start the day after day 1 ends,
// not silently reuse or collide with it.
// Defaults the TARGET picker to "Main" — Schedule.js's geScheduleNames()
// always synthesizes a "Main" entry regardless of whether it has any
// events yet, so as far as the user is concerned it always exists.
// Creating a second schedule needs to be something the user explicitly
// chooses, or they'd have no reason to know it exists and go looking for
// it. Found 2026-09-08.
const DEFAULT_TARGET = "Main";

const BuildScheduleDialog = ({ show, onHide, onConfirm, scheduleNames, defaultNewName, defaultStartDate, defaultEndDate, scheduleNextDates, destinationSummary }) => {
  const [target, setTarget] = useState(DEFAULT_TARGET);
  const [newName, setNewName] = useState(defaultNewName || "Trip");
  // Exact date+time now, not just a date — matches the precision
  // TripAI.js/BuildMyTripWizard.js use for a destination's dateStart/dateEnd,
  // replacing the old date-only "Start date" + "Number of days" pair with
  // one Start/End pair. Nothing gets scheduled after End (a hard ceiling on
  // the last scheduled day) — push it out to fit more places over more days.
  const [startDate, setStartDate] = useState(defaultStartDate || new Date());
  const [endDate, setEndDate] = useState(defaultEndDate || new Date());
  // Once the user has picked a Start or End themselves, changing the target
  // schedule leaves the dates alone. Picking the dates and then choosing
  // Replace "Main" used to put them back to the one-day default, so a
  // two-day build came out as one day (found 2026-10-13).
  const datesEdited = useRef(false);
  // Defaults to Relaxed (1.3), not Normal (1) — see BuildMyTripWizard.js.
  const [paceMultiplier, setPaceMultiplier] = useState("1.3");
  // Off by default — a new behavior change, not something every trip wants.
  // When on, reserves a real "Lunch" event (no linked place, same as a
  // manually-typed schedule item) around midday each day instead of
  // scheduling straight through it.
  const [wantLunch, setWantLunch] = useState(false);
  // The guide's Home/Itinerary document can reflect any schedule, not just
  // Main — same as the Schedule menu's own "Copy to Trip Itinerary" action,
  // which works on whichever schedule is currently selected. Defaults on so
  // the itinerary doesn't silently drift out of sync with the schedule it's
  // supposed to reflect.
  const [updateItinerary, setUpdateItinerary] = useState(true);
  // Folded in from the separate post-build "Add more detail?" modal
  // (PlacesHeader.js) — same optional Wikipedia-notes/opening-hours
  // enrichment tail BuildMyTripWizard.js's own schedule step offers, just
  // collected up front instead of as its own dialog after the build.
  const [enrichInfoWanted, setEnrichInfoWanted] = useState(true);
  const [enrichHoursWanted, setEnrichHoursWanted] = useState(true);

  useEffect(() => {
    if (show) {
      datesEdited.current = false;
      setTarget(DEFAULT_TARGET);
      setNewName(defaultNewName || "Trip");
      setStartDate(defaultStartDate || new Date());
      setEndDate(defaultEndDate || new Date());
      setPaceMultiplier("1.3");
      setWantLunch(false);
      setUpdateItinerary(true);
      setEnrichInfoWanted(true);
      setEnrichHoursWanted(true);
    }
  }, [show, defaultNewName, defaultStartDate, defaultEndDate]);

  const isReplace = target.startsWith(REPLACE_PREFIX);
  const targetName = isReplace ? target.slice(REPLACE_PREFIX.length) : target;

  const onTargetChange = (value) => {
    setTarget(value);
    if (datesEdited.current) return;
    // A replaced schedule starts from scratch, like a new one.
    if (value === NEW_SCHEDULE_VALUE || value.startsWith(REPLACE_PREFIX)) {
      setStartDate(defaultStartDate || new Date());
      setEndDate(defaultEndDate || new Date());
    } else if (scheduleNextDates?.[value]) {
      setStartDate(scheduleNextDates[value]);
    }
  };

  const confirm = () => {
    onConfirm({
      isNewSchedule: target === NEW_SCHEDULE_VALUE,
      replaceExisting: isReplace,
      scheduleName: target === NEW_SCHEDULE_VALUE ? (newName.trim() || defaultNewName || "Trip") : targetName,
      startDate,
      endDate,
      paceMultiplier: Number(paceMultiplier) || 1,
      wantLunch,
      updateItinerary,
      enrichInfoWanted,
      enrichHoursWanted,
    });
    onHide();
  };

  return (
    <div className="modal">
      <Modal show={show} onHide={onHide} animation={false} dialogClassName="bsd-dialog" aria-labelledby="bsd-title" centered>
        <Modal.Body style={{ padding: 0 }}>
          <div className="bsd-shell">
            <div className="bsd-header">
              <div className="bsd-header-icon">
                <FontAwesomeIcon icon={solid("calendar-days")} />
              </div>
              <h2 className="bsd-title" id="bsd-title">Build Trip</h2>
              <button className="bsd-close" onClick={onHide}>
                <FontAwesomeIcon icon={solid("xmark")} />
              </button>
            </div>

            <div className="bsd-body">
              {destinationSummary && destinationSummary.length > 0 && (
                <div className="bsd-row bsd-row-stack">
                  <span className="bsd-label">Destinations in this selection</span>
                  <span className="bsd-hint">
                    {destinationSummary
                      .map((d) => `${d.city}${d.country ? ", " + d.country : ""} (${d.count})`)
                      .join(" · ")}
                  </span>
                </div>
              )}

              <div className="bsd-row bsd-row-stack">
                <span className="bsd-label">Schedule</span>
                <select className="bsd-select" value={target} onChange={(e) => onTargetChange(e.target.value)}>
                  <option value={NEW_SCHEDULE_VALUE}>Create new schedule</option>
                  {(scheduleNames || []).map((s) => (
                    <option key={s.value} value={s.value}>Add to "{s.label}"</option>
                  ))}
                  {(scheduleNames || []).map((s) => (
                    <option key={REPLACE_PREFIX + s.value} value={REPLACE_PREFIX + s.value}>Replace "{s.label}"</option>
                  ))}
                </select>
                {target !== NEW_SCHEDULE_VALUE && !isReplace && (
                  <span className="bsd-hint">Start date below was set to the day after "{target}" currently ends.</span>
                )}
                {isReplace && (
                  <span className="bsd-hint">Everything now in "{targetName}" is removed, and the schedule is rebuilt from the places you selected.</span>
                )}
              </div>

              {target === NEW_SCHEDULE_VALUE && (
                <div className="bsd-row bsd-row-stack">
                  <span className="bsd-label">Name</span>
                  <input
                    className="bsd-input"
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    maxLength={60}
                  />
                </div>
              )}

              <div className="bsd-row bsd-row-stack">
                <span className="bsd-label">Start</span>
                <DatePicker showMonthDropdown showYearDropdown dropdownMode="select"
                  selected={startDate}
                  onChange={(date) => {
                    datesEdited.current = true;
                    setStartDate(date);
                    // Same clamp as DestinationsScreen.js's onChangeDestStart
                    // — pushing Start past the current End would otherwise
                    // leave an inverted (negative-length) range in place.
                    if (endDate < date) setEndDate(date);
                  }}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={5}
                  timeCaption="time"
                  dateFormat="MMM d, yyyy HH:mm"
                  className="bsd-input"
                  withPortal
                />
              </div>

              <div className="bsd-row bsd-row-stack">
                <span className="bsd-label">End</span>
                <span className="bsd-hint">Nothing gets scheduled after this — push it out to fit more places over more days.</span>
                <DatePicker showMonthDropdown showYearDropdown dropdownMode="select"
                  selected={endDate}
                  onChange={(date) => { datesEdited.current = true; setEndDate(date); }}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={5}
                  timeCaption="time"
                  dateFormat="MMM d, yyyy HH:mm"
                  className="bsd-input"
                  withPortal
                />
              </div>

              <div className="bsd-row">
                <label className="bsd-checkbox-row">
                  <input
                    type="checkbox"
                    checked={wantLunch}
                    onChange={(e) => setWantLunch(e.target.checked)}
                  />
                  <span>Leave room for lunch.</span>
                </label>
              </div>

              <div className="bsd-row">
                <label className="bsd-checkbox-row">
                  <input
                    type="checkbox"
                    checked={updateItinerary}
                    onChange={(e) => setUpdateItinerary(e.target.checked)}
                  />
                  <span>Update itinerary to match the schedule.</span>
                </label>
              </div>

              <div className="bsd-row bsd-row-stack">
                <span className="bsd-label">Pace</span>
                <select className="bsd-select" value={paceMultiplier} onChange={(e) => setPaceMultiplier(e.target.value)}>
                  {PACE_OPTIONS.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
                <span className="bsd-hint">Controls how long each stop gets — doesn't change the number of days.</span>
              </div>

              <div className="bsd-row bsd-row-stack">
                <span className="bsd-label">Add more detail</span>
                <span className="bsd-hint">Look up extra info for the places in this guide. This can take a minute or two — you can always do it later from the Places menu instead.</span>
                <label className="bsd-checkbox-row">
                  <input
                    type="checkbox"
                    checked={enrichInfoWanted}
                    onChange={(e) => setEnrichInfoWanted(e.target.checked)}
                  />
                  <span>Auto add info to Notes (from Wikipedia)</span>
                </label>
                <label className="bsd-checkbox-row">
                  <input
                    type="checkbox"
                    checked={enrichHoursWanted}
                    onChange={(e) => setEnrichHoursWanted(e.target.checked)}
                  />
                  <span>Add opening times (from Google)</span>
                </label>
              </div>
            </div>

            <div className="bsd-footer">
              <button className="bsd-btn bsd-btn-cancel" onClick={onHide}>Cancel</button>
              <button className="bsd-btn bsd-btn-action" onClick={confirm}>Build</button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default BuildScheduleDialog;
